// Redux/Slices/Pages/Provider/BusinessSlice.js
// Manages: provider's registered businesses and professional services
// BZV2 backend: /api/v2/business  /api/v2/professional

import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../../../../Common/Interceptors/AxiosConfig';
import { showNotification } from '../../../../Common/Toast/Toast';
import { fetchProfessionalTypes } from '../../Common/Catalog/CatalogSlice';

const initialState = {
  errors: null,
  isLoading: false,
  myBusinesses: [],           // renamed from myServices
  myBusinessesLoading: false,
  myBusinessesLastFetchedAt: null,
  success: false,
  businessDetails: null,      // renamed from serviceDetails
  businessDetailsLastFetchedAt: null,
};

const businessSlice = createSlice({
  name: 'business',           // renamed from providerService
  initialState,
  reducers: {
    setErrors: (state, action) => { state.errors = action.payload; },
    clearErrors: (state) => { state.errors = null; },
    setLoading: (state, action) => { state.isLoading = action.payload; },
    setMyBusinesses: (state, action) => {
      state.myBusinesses = action.payload;
      state.myBusinessesLastFetchedAt = Date.now();
    },
    setMyBusinessesLoading: (state, action) => { state.myBusinessesLoading = action.payload; },
    setSuccess: (state, action) => { state.success = action.payload; },
    setBusinessDetails: (state, action) => {
      state.businessDetails = action.payload;
      state.businessDetailsLastFetchedAt = Date.now();
    },
    resetBusinessDetails: (state) => {
      state.businessDetails = null;
      state.businessDetailsLastFetchedAt = null;
    },
  },
});

// ── Thunks ────────────────────────────────────────────────────────────────────

/**
 * Fetch professional types from CatalogSlice (public taxonomy, no auth needed)
 * Previously: hitting discovery/hierarchy directly — now uses CatalogSlice.
 */
export const loadProfessionalTypes = () => (dispatch) => {
  dispatch(fetchProfessionalTypes());
};

/**
 * Fetch the current provider's businesses or professional services.
 * Business users  → GET /business/my-businesses
 * Professional    → GET /professional/me/services
 */
export const getMyBusinesses = (force = false) => async (dispatch, getState) => {
  const { myBusinesses, myBusinessesLastFetchedAt, myBusinessesLoading } = getState().business;
  const FIVE_MINUTES = 5 * 60 * 1000;

  if (
    !force &&
    myBusinesses.length > 0 &&
    myBusinessesLastFetchedAt &&
    Date.now() - myBusinessesLastFetchedAt < FIVE_MINUTES
  ) return;

  if (myBusinessesLoading) return;

  dispatch(setMyBusinessesLoading(true));
  dispatch(setErrors(null));

  try {
    const user = getState().login?.userData;
    const isProfessional = user?.userDataType === 'professional';
    const url = isProfessional ? 'professional/me/services' : 'business/my-businesses';
    const response = await axiosInstance.get(url);
    const { success, data, message } = response?.data || {};

    if (success) {
      dispatch(setMyBusinesses(data));
      dispatch(setSuccess(true));
    } else {
      dispatch(setErrors({ message: message || 'Failed to fetch businesses' }));
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'Failed to fetch businesses';
    dispatch(setErrors({ message: errorMsg }));
  } finally {
    dispatch(setMyBusinessesLoading(false));
  }
};

/** GET /business/:id  or  GET /professional/services/:id */
export const getBusinessDetails = (businessId, force = false) => async (dispatch, getState) => {
  const { businessDetails, businessDetailsLastFetchedAt, isLoading } = getState().business;
  const cachedId = businessDetails?._id || businessDetails?.id;
  const FIVE_MIN = 5 * 60 * 1000;

  if (
    !force &&
    cachedId?.toString() === businessId &&
    businessDetailsLastFetchedAt &&
    Date.now() - businessDetailsLastFetchedAt < FIVE_MIN
  ) return;

  if (isLoading) return;
  dispatch(setLoading(true));
  dispatch(setErrors(null));

  try {
    const user = getState().login?.userData;
    const isProfessional = user?.userDataType === 'professional';
    const url = isProfessional ? `professional/services/${businessId}` : `business/${businessId}`;
    const response = await axiosInstance.get(url);
    const { success, data, message } = response?.data || {};

    if (success) {
      dispatch(setBusinessDetails(data));
      dispatch(setSuccess(true));
    } else {
      dispatch(setErrors({ message: message || 'Failed to fetch business details' }));
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'Failed to load business';
    dispatch(setErrors({ message: errorMsg }));
  } finally {
    dispatch(setLoading(false));
  }
};

/** POST /business */
export const createBusiness = (businessData) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setErrors(null));

  try {
    let finalPayload;

    // Industrial Context Check: If already FormData, pass through. Otherwise build it.
    if (businessData instanceof FormData) {
      finalPayload = businessData;
    } else {
      const formData = new FormData();

      // ─── Industrial Hierarchy IDs (BZV2) ──────────────────────────────────
      formData.append('workspaceClassId', businessData.workspaceClassId);
      formData.append('subCategoryId', businessData.subCategoryId);
      formData.append('categoryId', businessData.categoryId);

      formData.append('name', businessData.name);
      if (businessData.email) formData.append('email', businessData.email);
      formData.append('phone', businessData.phone.replace(/\D/g, ""));

      // Address nested mappings manually expanded for multer parsing
      if (businessData.address?.building) formData.append('address[street]', businessData.address.building);
      if (businessData.address?.area) formData.append('address[area]', businessData.address.area);
      if (businessData.address?.city) formData.append('address[city]', businessData.address.city);
      if (businessData.address?.state) formData.append('address[state]', businessData.address.state);
      if (businessData.address?.pincode) formData.append('address[pincode]', businessData.address.pincode);

      if (businessData.locationData?.latitude && businessData.locationData?.longitude) {
        formData.append('address[latitude]', businessData.locationData.latitude);
        formData.append('address[longitude]', businessData.locationData.longitude);
      }

      if (businessData.description) formData.append('description', businessData.description);

      if (businessData.profilePicture) {
        formData.append('profilePicture', businessData.profilePicture); 
      }

      if (businessData.workspaceId) {
        formData.append('workspaceId', businessData.workspaceId);
      }
      
      finalPayload = formData;
    }

    const response = await axiosInstance.post('workspaces/business', finalPayload, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const { success, data, message } = response?.data || {};

    if (success) {
      dispatch(getMyBusinesses(true));
      dispatch(setSuccess(true));
      showNotification({ message: message || 'Business created successfully', type: 'success' });
      return { success: true, message, data };
    } else {
      const errorMsg = message || 'Failed to create business';
      dispatch(setErrors({ message: errorMsg }));
      showNotification({ message: errorMsg, type: 'error', theme: 'light' });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'Creation failed';
    dispatch(setErrors({ message: errorMsg }));
    showNotification({ message: errorMsg, type: 'error', theme: 'light' });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/** POST /workspaces/:id/dsa */
export const createWorkspaceDSA = (workspaceId, parameters) => async (dispatch) => {
  try {
    const response = await axiosInstance.post(`workspaces/${workspaceId}/dsa`, { parameters });
    const { success, message, data } = response?.data || {};
    if (success) {
      return { success: true, message, data };
    }
    return { success: false, message: message || 'Failed to save DSA' };
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'DSA Creation failed';
    showNotification({ message: errorMsg, type: 'error', theme: 'light' });
    return { success: false, message: errorMsg };
  }
};

/** PATCH /business/:id  or  /professional/services/:id */
export const updateBusinessProfile = (businessId, updateData) => async (dispatch, getState) => {
  dispatch(setLoading(true));
  dispatch(setErrors(null));

  try {
    let finalPayload;
    if (updateData instanceof FormData) {
      finalPayload = updateData;
    } else {
      const formData = new FormData();
      Object.keys(updateData).forEach(key => {
        if (key === 'address' && typeof updateData[key] === 'object') {
          Object.keys(updateData[key]).forEach(addrKey => {
            formData.append(`address[${addrKey}]`, updateData[key][addrKey]);
          });
        } else if (key === 'logo' || key === 'imageFile' || key === 'coverImage' || key === 'profilePicture') {
          if (updateData[key] instanceof File) {
            formData.append('profilePicture', updateData[key]);
          }
        } else if (key === 'workspaceId') {
          formData.append('workspaceId', updateData[key]);
        } else {
          formData.append(key, updateData[key]);
        }
      });
      finalPayload = formData;
    }

    const user = getState().login?.userData;
    const isProfessional = user?.userDataType === 'professional';
    const url = isProfessional ? `workspaces/professional/${businessId}` : `workspaces/business/${businessId}`;

    const response = await axiosInstance.patch(url, finalPayload, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    const { success, data, message } = response?.data || {};

    if (success) {
      dispatch(setBusinessDetails(data));
      dispatch(getMyBusinesses(true));
      dispatch(setSuccess(true));
      showNotification({ message: message || 'Business updated successfully', type: 'success' });
      return { success: true, message, data };
    } else {
      const errorMsg = message || 'Failed to update business';
      dispatch(setErrors({ message: errorMsg }));
      showNotification({ message: errorMsg, type: 'error', theme: 'light' });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'Update failed';
    dispatch(setErrors({ message: errorMsg }));
    showNotification({ message: errorMsg, type: 'error', theme: 'light' });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/** POST /business/whatsapp/otp/request */
export const requestBusinessPhoneOTP = (businessId, phoneNumber) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setErrors(null));

  try {
    let phone = phoneNumber.replace(/\D/g, '');
    if (phone.startsWith('91') && phone.length === 12) phone = phone.substring(2);
    if (!phone || phone.length !== 10) {
      const errorMsg = 'Please enter a valid 10-digit WhatsApp number';
      showNotification({ message: errorMsg, type: 'error', theme: 'light' });
      return { success: false, message: errorMsg };
    }
    const response = await axiosInstance.post('business/whatsapp/otp/request', { whatsappNumber: phone });
    const { success, data, message } = response?.data || {};
    if (success) {
      showNotification({ message: message || 'OTP sent successfully', type: 'success' });
      return { success: true, message, data };
    }
    const errorMsg = message || 'Failed to send OTP';
    showNotification({ message: errorMsg, type: 'error', theme: 'light' });
    return { success: false, message: errorMsg };
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'Failed to send OTP';
    showNotification({ message: errorMsg, type: 'error', theme: 'light' });
    dispatch(setErrors({ message: errorMsg }));
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/** POST /business/whatsapp/otp/verify */
export const verifyBusinessPhoneOTP = (businessId, phoneNumber, otp) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setErrors(null));

  try {
    let phone = phoneNumber.replace(/\D/g, '');
    if (phone.startsWith('91') && phone.length === 12) phone = phone.substring(2);
    if (!phone || phone.length !== 10) {
      const errorMsg = 'Invalid WhatsApp number';
      showNotification({ message: errorMsg, type: 'error', theme: 'light' });
      return { success: false, message: errorMsg };
    }
    const response = await axiosInstance.post('business/whatsapp/otp/verify', { whatsappNumber: phone, otp });
    const { success, data, message } = response?.data || {};
    if (success) {
      dispatch(getBusinessDetails(businessId));
      showNotification({ message: message || 'WhatsApp verified successfully', type: 'success' });
      return { success: true, message, data };
    }
    const errorMsg = message || 'OTP verification failed';
    showNotification({ message: errorMsg, type: 'error', theme: 'light' });
    return { success: false, message: errorMsg };
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'Invalid OTP';
    showNotification({ message: errorMsg, type: 'error', theme: 'light' });
    dispatch(setErrors({ message: errorMsg }));
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/** DELETE /business/:id */
export const requestBusinessDeletion = (businessId) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setErrors(null));

  try {
    const response = await axiosInstance.delete(`business/${businessId}`);
    const { success, message } = response?.data || {};
    if (success) {
      showNotification({ message: message || 'Business deletion scheduled', type: 'success' });
      dispatch(getMyBusinesses(true));
      return { success: true, message };
    }
    const errorMsg = message || 'Failed to schedule deletion';
    dispatch(setErrors({ message: errorMsg }));
    showNotification({ message: errorMsg, type: 'error', theme: 'light' });
    return { success: false, message: errorMsg };
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'Deletion request failed';
    dispatch(setErrors({ message: errorMsg }));
    showNotification({ message: errorMsg, type: 'error', theme: 'light' });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/** POST /business/:id/reactivate */
export const reactivateBusinessAction = (businessId) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setErrors(null));

  try {
    const response = await axiosInstance.post(`workspaces/me/Business/${businessId}/reactivate`);
    const { success, message } = response?.data || {};
    if (success) {
      showNotification({ message: message || 'Business reactivated successfully', type: 'success' });
      dispatch(getBusinessDetails(businessId, true));
      dispatch(getMyBusinesses(true));
      return { success: true, message };
    }
    const errorMsg = message || 'Failed to reactivate';
    dispatch(setErrors({ message: errorMsg }));
    showNotification({ message: errorMsg, type: 'error', theme: 'light' });
    return { success: false, message: errorMsg };
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'Reactivation failed';
    dispatch(setErrors({ message: errorMsg }));
    showNotification({ message: errorMsg, type: 'error', theme: 'light' });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

export const {
  setErrors, clearErrors, setLoading,
  setMyBusinesses, setMyBusinessesLoading,
  setSuccess, setBusinessDetails, resetBusinessDetails,
} = businessSlice.actions;

export default businessSlice.reducer;
